import { useCallback } from 'react';

import { OPTIMIZED_HOME_OFFERS_FILTER, useOffers } from '@ocx/data-offers';
import type { OfferPresentationPriority } from '@ocx/graphql';
import { useAltriaLocations, useOffersLocationsGeoFilter } from '@ocx/feat-local-offers';
// eslint-disable-next-line @nx/enforce-module-boundaries -- matches useOfferCategories; needs app retailer config
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';

import { useOfferCategories } from './use-offer-categories.hook';

export type UseOffersCatalogParams = {
  /**
   * When set, passed to `useOffers` (e.g. Offers tab uses `Default` only).
   * When omitted, matches Home grid: no explicit priority filter on the query.
   */
  presentationPriorityIncludes?: OfferPresentationPriority[];
  /** When true, persists successful query results to localforage for warm-start seeding. */
  persistToStorage?: boolean;
};

export const useOffersCatalog = (params: UseOffersCatalogParams = {}) => {
  const { presentationPriorityIncludes, persistToStorage } = params;
  const {
    config: {
      localOffers,
      loyaltyProgram: { homePageOffersLimit },
    },
  } = useConfiguration();
  const isLocalOffersFeatureEnabled = localOffers.enabled;

  const locationsGeoFilter = useOffersLocationsGeoFilter({
    includeCoordinatesWhenShowingAll: false,
  });

  const {
    offers,
    loading: offersLoading,
    refetch: refetchOffers,
  } = useOffers({
    variables: {
      ...OPTIMIZED_HOME_OFFERS_FILTER,
      first: homePageOffersLimit,
      locationsGeoFilter,
      ...(presentationPriorityIncludes !== undefined ? { presentationPriorityIncludes } : {}),
    },
    persistToStorage,
  });

  const { categories, selectedCategory, onCategoryChange, refetchCategories } = useOfferCategories(offers, {
    persistToStorage,
  });

  const hasAltriaOffers = !!(selectedCategory && selectedCategory.banners && selectedCategory.banners.length > 0);
  const { hasAltriaOffersNearby } = useAltriaLocations({
    skip: !hasAltriaOffers,
  });

  const refetchCatalog = useCallback(async () => {
    await Promise.all([refetchOffers(), refetchCategories()]);
  }, [refetchOffers, refetchCategories]);

  return {
    offers,
    offersLoading,
    refetchOffers,
    refetchCategories,
    refetchCatalog,
    categories,
    selectedCategory,
    onCategoryChange,
    isLocalOffersFeatureEnabled,
    hasAltriaOffersNearby,
  };
};
